import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { confirmSignUp } from '../services/authService';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Link,
    Fade,
    Slide,
    CircularProgress
} from '@mui/material';

export const ConfirmSignUpPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;
    
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    if (!email) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default'
                }}
            >
                <Card sx={{ p: 4, maxWidth: 400, width: '90%' }}>
                    <Typography variant="h5" gutterBottom>
                        Error
                    </Typography>
                    <Typography color="error" paragraph>
                        No email address found. Please try signing up again.
                    </Typography>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => navigate('/signup')}
                    >
                        Go to Sign Up
                    </Button>
                </Card>
            </Box>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await confirmSignUp(email, code);
            // Redirect to login with success message
            navigate('/login', {
                state: {
                    message: 'Email verified successfully! You can now log in.'
                }
            });
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
            }}
        >
            {/* Ocean Video Background */}
            <Box
                component="video"
                autoPlay
                muted
                loop
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: -2
                }}
            >
                <source src="/loginwaves.mp4" type="video/mp4" />
            </Box>

            {/* Semi-transparent overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(144,224,239,0.2) 50%, rgba(0,0,0,0.4) 100%)',
                    zIndex: -1
                }}
            />

            {/* Glassmorphism Card */}
            <Card
                sx={{
                    minWidth: { xs: '90%', sm: 400 },
                    maxWidth: 450,
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 4,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    p: 2
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    {/* App Branding */}
                    <Box textAlign="center" mb={4}>
                        <Typography
                            variant="h3"
                            sx={{
                                color: 'white',
                                fontWeight: 600,
                                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                                mb: 1
                            }}
                        >
                            Waves Within
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontWeight: 300,
                                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                            }}
                        >
                            Mantras • Meditation • Ocean
                        </Typography>
                    </Box>

                    {/* Form */}
                    <Fade in={true} timeout={500}>
                        <Box component="form" onSubmit={handleSubmit}>
                            <Typography
                                variant="h5"
                                textAlign="center"
                                sx={{ color: 'white', mb: 2, fontWeight: 500 }}
                            >
                                Verify Your Email
                            </Typography>

                            <Typography
                                textAlign="center"
                                sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 3 }}
                            >
                                We've sent a verification code to {email}
                            </Typography>

                            {error && (
                                <Typography
                                    color="error"
                                    sx={{
                                        mb: 2,
                                        p: 1,
                                        borderRadius: 1,
                                        bgcolor: 'rgba(211, 47, 47, 0.1)',
                                        textAlign: 'center'
                                    }}
                                >
                                    {error}
                                </Typography>
                            )}

                            <Slide direction="up" in={true} timeout={600}>
                                <TextField
                                    fullWidth
                                    name="code"
                                    label="Verification Code"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    required
                                    sx={{
                                        mb: 3,
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            color: 'white',
                                            '& fieldset': {
                                                borderColor: 'rgba(255, 255, 255, 0.3)'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'rgba(255, 255, 255, 0.5)'
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#90E0EF'
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            '&.Mui-focused': {
                                                color: '#90E0EF'
                                            }
                                        }
                                    }}
                                />
                            </Slide>

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    mt: 2,
                                    mb: 3,
                                    py: 1.5,
                                    background: 'linear-gradient(45deg, #90E0EF 30%, #F4A261 90%)',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                    boxShadow: '0 4px 20px rgba(144, 224, 239, 0.4)',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, #7DD3FC 30%, #FB923C 90%)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 25px rgba(144, 224, 239, 0.6)'
                                    }
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    'Verify Email'
                                )}
                            </Button>

                            <Box textAlign="center">
                                <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                    Didn't receive the code?{' '}
                                    <Link
                                        component="button"
                                        type="button"
                                        onClick={() => navigate('/signup')}
                                        sx={{
                                            color: '#90E0EF',
                                            textDecoration: 'none',
                                            fontWeight: 500,
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        Try signing up again
                                    </Link>
                                </Typography>
                            </Box>
                        </Box>
                    </Fade>
                </CardContent>
            </Card>
        </Box>
    );
}; 